import type { DataProvider } from "ra-core";

import type {
  FinanceCharge,
  FinanceContract,
  FinanceExpense,
  FinanceRecurringExpense,
} from "../../types";
import { buildChargesForMonth, buildExpensesForMonth } from "./financeSchedule";

/** Upper bound of one read; a team has far fewer contracts or templates */
const MAX_RECORDS = 1000;
const SORT_BY_ID = { field: "id", order: "ASC" } as const;

export interface FinanceMonthGeneration {
  charges: number;
  expenses: number;
  /** Rows that could not be created (e.g. created meanwhile by someone else) */
  failed: number;
}

const listAll = async <T extends { id: unknown }>(
  dataProvider: DataProvider,
  resource: string,
  filter: Record<string, unknown>,
): Promise<T[]> => {
  const { data } = await dataProvider.getList(resource, {
    pagination: { page: 1, perPage: MAX_RECORDS },
    sort: SORT_BY_ID,
    filter,
  });
  return data as T[];
};

/**
 * Creates the charges (monthly fees, setup installments) and the recurring
 * expenses of `month` that do not exist yet. Idempotent: the rows already there
 * are skipped, and the unique constraints of the database reject a concurrent
 * duplicate, which is then counted in `failed` instead of failing the whole run.
 */
export const generateFinanceMonth = async (
  dataProvider: DataProvider,
  month: string,
): Promise<FinanceMonthGeneration> => {
  const [contracts, charges, templates, expenses] = await Promise.all([
    listAll<FinanceContract>(dataProvider, "finance_contracts", {
      "status@neq": "cancelled",
    }),
    listAll<FinanceCharge>(dataProvider, "finance_charges", {
      competence_month: month,
    }),
    listAll<FinanceRecurringExpense>(
      dataProvider,
      "finance_recurring_expenses",
      { active: true },
    ),
    listAll<FinanceExpense>(dataProvider, "finance_expenses", {
      competence_month: month,
    }),
  ]);
  const newCharges = buildChargesForMonth(contracts, charges, month);
  const newExpenses = buildExpensesForMonth(templates, expenses, month);
  const results = await Promise.allSettled([
    ...newCharges.map((data) =>
      dataProvider.create("finance_charges", { data }),
    ),
    ...newExpenses.map((data) =>
      dataProvider.create("finance_expenses", { data }),
    ),
  ]);
  results.forEach((result) => {
    if (result.status === "rejected") {
      console.warn("generateFinanceMonth", result.reason);
    }
  });
  const countCreated = (slice: PromiseSettledResult<unknown>[]) =>
    slice.filter((result) => result.status === "fulfilled").length;
  const createdCharges = countCreated(results.slice(0, newCharges.length));
  const createdExpenses = countCreated(results.slice(newCharges.length));
  return {
    charges: createdCharges,
    expenses: createdExpenses,
    failed: results.length - createdCharges - createdExpenses,
  };
};
